import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { Appointment } from '@/lib/api'
import { STATUS_VARIANT } from './constants'
import { isPastStart } from './calendarHelpers'
import { PresetSelect } from './PresetSelect'

function toDateInput(epoch: number) {
  const d = new Date(epoch * 1000)
  return d.toISOString().split('T')[0]
}

export interface EditApptFormBody {
  title?: string
  location?: string
  notes?: string
}

export interface EditApptFormFieldsProps {
  appt: Appointment
  onClose: () => void
  onSubmit: (body: EditApptFormBody) => void
  /** 硬删除（仅 scheduled 显示入口），父级负责调用 DELETE 接口 */
  onDelete?: () => void
  /** 标记完成（仅过期 scheduled 显示），父级负责调用 complete 接口 */
  onComplete?: () => void
  /** 标记未出席（仅过期 scheduled 显示，已扣课时退回），父级负责调用 no-show 接口 */
  onNoShow?: () => void
  /** 更改时间（仅 scheduled 显示，OPT-013：与拖拽同走 /move 直接生效） */
  onMove?: () => void
}

export interface EditApptFormProps extends Omit<EditApptFormFieldsProps, 'appt'> {
  open: boolean
  appt: Appointment | null
}

/** 编辑预约表单主体（无外壳）：桌面右侧面板与移动端 Dialog 共用 */
export function EditApptFormFields({ appt, onClose, onSubmit, onDelete, onComplete, onNoShow, onMove }: EditApptFormFieldsProps) {
  const { t } = useTranslation()
  const [location, setLocation] = useState(appt.location || '')
  const [notes, setNotes] = useState(appt.notes || '')

  // OPT-020：保存按钮仅在表单有实际变更时激活
  const hasChanges = useMemo(
    () =>
      location !== (appt.location || '') ||
      notes !== (appt.notes || ''),
    [location, notes, appt.location, appt.notes],
  )

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit({
          location: location || undefined,
          notes: notes || undefined,
        })
      }}
    >
      <div className="space-y-3">
        <div className="text-sm text-slate-500">
          {t('schedule.form.status')}:{' '}
          <Badge variant={STATUS_VARIANT[appt.status]}>{t(`schedule.status.${appt.status}`)}</Badge>
        </div>
        <div className="text-sm text-slate-500">
          {toDateInput(appt.scheduledDate)} {appt.startTime} - {appt.endTime}
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="edit-location">{t('schedule.form.location')}</Label>
            <PresetSelect kind="location" onSelect={(text) => setLocation(text)} />
          </div>
          <Input
            id="edit-location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="edit-notes">{t('common.notes')}</Label>
            <PresetSelect kind="note" onSelect={(text) => setNotes(text)} />
          </div>
          <Input
            id="edit-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>
      {/* OPT-020：按钮顺序——删除 / 更改时间 / 保存，保存仅在变更时激活 */}
      <div className="mt-4 flex items-center gap-2">
        {/* 硬删除入口：仅 scheduled 显示 */}
        {appt.status === 'scheduled' && onDelete && (
          <Button
            type="button"
            variant="destructive"
            className="mr-auto"
            data-testid="appt-delete"
            onClick={() => {
              if (window.confirm(t('schedule.confirmDelete'))) onDelete()
            }}
          >
            {t('common.delete')}
          </Button>
        )}
        {/* 过期课程手动结算：已完成 / 未出席 */}
        {appt.status === 'scheduled' && isPastStart(appt) && (onComplete || onNoShow) && (
          <div className="mr-auto flex gap-2">
            {onComplete && (
              <Button type="button" variant="outline" data-testid="appt-complete" onClick={() => onComplete()}>
                {t('schedule.actions.complete')}
              </Button>
            )}
            {onNoShow && (
              <Button
                type="button" variant="outline" data-testid="appt-noshow"
                onClick={() => { if (window.confirm(t('schedule.confirmNoShow'))) onNoShow() }}
              >
                {t('schedule.actions.noShow')}
              </Button>
            )}
          </div>
        )}
        {/* 更改时间入口：仅 scheduled 显示 */}
        {appt.status === 'scheduled' && onMove && (
          <Button type="button" variant="outline" data-testid="appt-move" onClick={() => onMove()}>
            {t('schedule.actions.moveTime')}
          </Button>
        )}
        <Button type="submit" variant={hasChanges ? 'default' : 'outline'} disabled={!hasChanges}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  )
}

/** 移动端/旧入口：Dialog 包装（行为与 props 不变） */
export function EditApptForm({ open, appt, onClose, ...fieldsProps }: EditApptFormProps) {
  const { t } = useTranslation()
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('schedule.editTitle')}</DialogTitle>
        </DialogHeader>
        {appt && <EditApptFormFields appt={appt} onClose={onClose} {...fieldsProps} />}
      </DialogContent>
    </Dialog>
  )
}
